import { ButtonType } from '@/components/CmgButton/cmg-button';
import { formatOfferEndsDate, isDesktop } from '@/helpers';
import { Campaign, CampaignTypes, CustomerProfile, GroupCampaign } from '@/models';
import { ui } from '@/constants'
import { Routes } from '@/router';
import { swiper, SwiperComponent, swiperSlide } from 'vue-awesome-swiper';
import { SwiperOptions } from 'swiper';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { ExtraOptinEvent } from '@/services/analytics/events/extra-optin-event';
import { AnalyticsEventType } from '@/services/analytics/platforms/adobe-analytics/models';

@Component({
  name: 'CmgRewardExtrasGroupCampaignDetailModal',
  components: {
    swiper,
    swiperSlide
  }
})
export default class CmgRewardExtrasGroupCampaignDetailModal extends Vue {
  slidesPerView: number = 3;

  extrasSwiper: SwiperComponent | null = null;
  swiperOptions: SwiperOptions = {
    slidesPerView: this.slidesPerView,
    spaceBetween: 5,
    watchOverflow: true,
    breakpoints: {
      1100: {
        slidesPerView: 2,
        spaceBetween: 5
      },
      700: {
        slidesPerView: 1,
        spaceBetween: 15
      }
    },
    pagination: {
      el: '.swiper-pagination',
    },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
      disabledClass: 'swiper-button-disabled'
    }
  };

  isSubmitted: boolean = false;
  selectedCampaignId: number = 0;
  errorState: boolean = false;

  get showPageArrows(): boolean {
    return this.displayCampaigns.length > this.slidesPerView;
  }

  get swiperButtonPrevId(): string {
    return 'swiper-button-prev';
  }

  get swiperButtonNextId(): string {
    return 'swiper-button-next';
  }

  get groupCampaign(): GroupCampaign {
    return this.$appStore.state.ui.modals.rewardExtrasGroupCampaignDetail.args!.groupCampaign;
  }

  get noCampaignSelected(): boolean {
    return this.selectedCampaignId === 0;
  }

  get selectedCampaign(): Campaign | null {
    if (this.selectedCampaignId > 0) {
      return this.groupCampaign.campaigns.filter(x => x.id === this.selectedCampaignId)[0];
    } else {
      return null;
    }
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.rewardExtrasGroupCampaignDetail.visible;
  }

  get isOptedIn(): boolean {
    return this.groupCampaign.isOptedIn;
  }

  get endDate(): Date | undefined {
    return this.groupCampaign.campaigns[0]?.endDate;
  }

  get offerEndDate(): string {
    if (this.endDate) {
      return formatOfferEndsDate(new Date(this.endDate));
    }
  
    return '';
  }

  get extraPoints(): number {
    return this.groupCampaign.campaigns[0]?.extraPoints;
  }

  get displayCampaigns(): Campaign[] {
    return this.groupCampaign.campaigns;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  @Watch('visible', { immediate: true })
  onStateChanged() {
    this.selectedCampaignId = 0;
    this.$nextTick(() => {
      const swiperRef = this.$refs.extrasSwiper as SwiperComponent | undefined;

      if (swiperRef) {
        this.extrasSwiper = swiperRef;

        if (this.prevIndex) {
          this.extrasSwiper.swiper?.slideTo(this.prevIndex);
        }

        if (this.showPageArrows) {
          const previousButton = document.getElementById(this.swiperButtonPrevId);
          previousButton!.style.backgroundImage = `url(${this.$appStore.state.cms.images.icons.chevronLeft.default})`;

          const nextButton = document.getElementById(this.swiperButtonNextId);
          nextButton!.style.backgroundImage = `url(${this.$appStore.state.cms.images.icons.chevronLeft.default})`;

          const swiperWrapper = document.getElementsByClassName('swiper-wrapper')[0];
          swiperWrapper.classList.add('has-page-arrows');
        }
      }
    });
  }  

  mounted() {
    this.extrasSwiper = this.$refs.extrasSwiper as SwiperComponent | null;
  }

  get swiperRealIndex(): number | undefined {
    return this.extrasSwiper?.swiper?.realIndex;
  }

  get prevIndex(): number | undefined {
    return 0;
  }

  goToPreviousSlide() {
    if (!this.extrasSwiper || !this.extrasSwiper.swiper) {
      return;
    }

    this.extrasSwiper.swiper.slidePrev();
  }

  goToNextSlide() {
    if (!this.extrasSwiper || !this.extrasSwiper.swiper) {
      return;
    }

    this.extrasSwiper.swiper.slideNext();
  }

  onClose() {
    this.$services.ui.setModalVisibility('rewardExtrasGroupCampaignDetail', false);
    this.errorState = false;
  }

  onCampaignSelected(campaignId: number): void {
    this.selectedCampaignId = campaignId;
    this.errorState = false;
  }

  async onClickCta(event: Event) {
    if (!this.selectedCampaign) {
      this.errorState = true;
      return;
    }

    this.isSubmitted = true;
    
    await this.$services.reward.optIntoCampaign(this.selectedCampaignId).then(async data => {
      if(data?.awarded) {
        if (!this.customer?.optedIntoExtras) {
          await this.$services.customer.updateCustomer({
            optedIntoExtras: true
          });
          this.customer!.optedIntoExtras = true;
        }

        this.selectedCampaign!.isOptedIn = true;
        this.groupCampaign.isOptedIn = true;

        const campaign = this.selectedCampaign!;

        const index = this.groupCampaign.campaigns.indexOf(campaign) + 1;
        this.$services.analytics.send(new ExtraOptinEvent(AnalyticsEventType.ActivateOffer, campaign, true, index));

        this.$services.ui.setModalVisibility('rewardExtrasGroupCampaignDetail', false);
        this.$services.ui.setModalVisibility('rewardExtrasCampaignDetail', true, { campaign }); 
      }
    })
    .catch(error => {
      const onClose = () => {
        this.$services.ui.setModalVisibility('basicError', false);
      };

      this.$services.ui.setModalVisibility('basicError', true, {
        header: `OUR BAD`,
        content: 'There was a problem opting into your extra. Give it a sec and try again.',
        background: 'white',
        options: [
          {
            text: 'Okay',
            type: ButtonType.Primary,
            onClick: onClose,
          },
        ],
        onClose,
      });
    })
    .finally(() => {
      this.isSubmitted = false;
      return false;
    })
  }

}